import { FC } from 'react';
import { Bot, Send } from 'lucide-react';
import Image from 'next/image';

interface PreviewProps {
  popupGreeting?: string;
  popupHeadline?: string;
  optionALabel?: string;
  optionBLabel?: string;
  optionCLabel?: string;
  primaryColor?: string;
  /** Popup text color (greeting, name, button labels). Defaults to white. */
  textColor?: string;
  /** Bot icon URL from customizationdata.botIcon.uploadedUri — falls back to generic icon. */
  botIconUrl?: string;
  /** Bot display name — must use the server's precedence (name, then botName). */
  botName?: string;
  /**
   * The global `customizationdata.welcomeMessage`. Shown when this page has no
   * headline/greeting override, because that is exactly what the live popup
   * falls back to (chatbotController.handlePageVisit). Without it the preview
   * showed placeholder copy the vendor had never configured.
   */
  fallbackMessage?: string;
  /**
   * True once the bot has engaged with the Site-wide popup editor at all (key
   * PRESENCE in customizationdata, not truthiness of the values — mirrors
   * handlePageVisit's hasSitewidePopupConfig exactly). When true, popupHeadline/
   * popupGreeting render exactly as given with NO fallback to fallbackMessage,
   * even if both are blank — a vendor may deliberately want a headline with no
   * second line, and the preview must show that (nothing), not silently
   * substitute the in-chat welcome message just because the fields look empty.
   * Leave false/unset for the specific-pages (per-page override) case, where the
   * live popup's fallback rule is genuinely truthiness-based, unchanged.
   */
  forceNoFallback?: boolean;
}

/**
 * Faithful preview of the pre-chat engage popup as it actually renders on the
 * vendor's site (matches EngageMessage.tsx default variant: coloured card,
 * avatar, greeting text, compact chip CTAs, chat input).
 */
const Preview: FC<PreviewProps> = ({
  popupGreeting,
  popupHeadline,
  optionALabel,
  optionBLabel,
  optionCLabel,
  primaryColor = '#0066CC',
  textColor = '#ffffff',
  botIconUrl,
  botName = 'Assistant',
  fallbackMessage,
  forceNoFallback = false,
}) => {
  const buttons = [optionALabel, optionBLabel, optionCLabel].filter(Boolean) as string[];

  // No per-page override → render the global welcome message the way the live
  // popup renders it: a single regular-weight paragraph, not a bold headline
  // with dimmed subtext. forceNoFallback (Site-wide mode) skips this entirely —
  // see the prop doc above.
  const hasOverride = forceNoFallback || Boolean(popupHeadline?.trim() || popupGreeting?.trim());
  const headline = hasOverride ? popupHeadline?.trim() : '';
  const greeting = hasOverride ? popupGreeting?.trim() : (fallbackMessage?.trim() || '');

  return (
    <div className='flex flex-col gap-3 w-full'>
      <p className='text-xs font-medium text-gray-400 uppercase tracking-wide text-center'>Preview</p>

      {/* Popup card — matches EngageMessage default variant */}
      <div
        className='w-full max-w-[300px] mx-auto rounded-xl overflow-hidden'
        style={{ boxShadow: 'rgba(0,0,0,0.15) 0px 5px 20px 0px' }}
      >
        {/* Coloured header */}
        <div
          className='p-4'
          style={{ backgroundColor: primaryColor }}
        >
          {/* Agent row */}
          <div className='flex items-center gap-2.5 mb-3'>
            <div className='w-9 h-9 rounded-full bg-white/20 flex items-center justify-center shrink-0 overflow-hidden'>
              {botIconUrl ? (
                <Image
                  src={botIconUrl}
                  alt={botName}
                  width={36}
                  height={36}
                  className='w-full h-full object-cover rounded-full'
                />
              ) : (
                <Bot className='h-5 w-5 text-white' />
              )}
            </div>
            <span className='text-sm font-semibold' style={{ color: textColor }}>{botName}</span>
          </div>

          {/* Greeting */}
          {headline && (
            <p className='text-sm font-semibold leading-snug' style={{ color: textColor }}>{headline}</p>
          )}
          {greeting && (
            <p
              className={headline ? 'text-xs mt-1 leading-relaxed' : 'text-xs leading-relaxed'}
              style={{ color: textColor, opacity: headline ? 0.85 : 1 }}
            >
              {greeting}
            </p>
          )}

          {/* CTA chip buttons */}
          {buttons.length > 0 && (
            <div className='flex flex-wrap gap-1.5 mt-3'>
              {buttons.map((label, i) => (
                <span
                  key={i}
                  className='inline-block text-xs font-medium px-3 py-1 rounded-full cursor-pointer transition-colors'
                  style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: textColor, border: `1px solid ${textColor}40` }}
                >
                  {label}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Input row */}
        <div className='bg-white px-3 py-2.5 flex items-center gap-2'>
          <input
            readOnly
            placeholder='Ask a question'
            className='flex-1 text-xs text-gray-400 bg-transparent outline-none placeholder-gray-400'
          />
          <div
            className='w-6 h-6 rounded-full flex items-center justify-center shrink-0'
            style={{ backgroundColor: primaryColor }}
          >
            <Send className='h-3 w-3 text-white' />
          </div>
        </div>
      </div>

      {/* No buttons is a valid, saveable configuration — not an unfinished form — so say
          what the visitor will actually get rather than prompting for input. */}
      {buttons.length === 0 && (
        <p className='text-center text-xs text-gray-400 mt-1'>
          No CTA buttons — visitors see the greeting and the message box only.
        </p>
      )}
    </div>
  );
};

export default Preview;
